import { firefuncs, auth, db } from "config/firebase";
import { httpsCallable } from "firebase/functions";
import {
  GoogleAuthProvider,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  sendEmailVerification as firebaseSendEmailVerification,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { addUserAndDocsToDB } from "_DatabaseFunctions/directConnect/users";

interface SignUpData {
  email: string;
  password: string;
  displayName: string;
  city: string;
  state: string;
}

interface CloudFunctionResponse {
  status: string;
  data: any;
}

export const createUser = async (signUpData: SignUpData): Promise<any> => {
  console.log("Creating user with data:", signUpData);

  // Validate required fields
  if (!signUpData.email || !signUpData.email.includes("@")) {
    throw new Error("Invalid email format in createUser");
  }

  const createUserFunc = httpsCallable(firefuncs, "createUser");
  const res = await createUserFunc(signUpData);
  console.log("create User res", res.data);
  const { status, data } = res.data as CloudFunctionResponse;

  if (status === "success") {
    return data;
  }
  if (status === "error") {
    console.log("error data", data);
    throw new Error(data);
  } else {
    throw new Error("Response Error");
  }
};

interface EmailVerificationParams {
  email: string;
  displayName: string;
}

export const sendEmailVerification = ({
  email,
  displayName,
}: EmailVerificationParams): Promise<any> => {
  console.log("Sending email verification for:", { email, displayName });

  // Validate email format before sending
  if (!email || typeof email !== "string" || !email.includes("@")) {
    throw new Error("Invalid email format provided");
  }

  const sendEmail = httpsCallable(firefuncs, "sendEmailVerification");
  return sendEmail({ email, displayName })
    .then((res) => {
      console.log("send verification email res", res.data);
      const { status, data } = res.data as CloudFunctionResponse;
      if (status === "error") throw new Error(data.errorInfo.message);
      return res;
    })
    .catch((err) => {
      console.log("Error sending verification email", err);
      throw err;
    });
};

// Google Sign-in function
export const signInWithGoogle = async (
  rememberMe: boolean = false,
): Promise<any> => {
  try {
    console.log("Attempting Google sign-in");

    // Set persistence based on user preference
    const persistence = rememberMe
      ? browserLocalPersistence
      : browserSessionPersistence;
    await setPersistence(auth, persistence);

    // Create Google provider
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: "select_account",
    });

    // Sign in with popup
    const result = await signInWithPopup(auth, provider);
    const { user } = result;

    console.log("Google sign-in successful:", user.email);

    // Check if user document exists in Firestore
    const userDocRef = doc(db, "users", user.uid);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      console.log("User document doesn't exist, creating new user document...");
      // Create user document with Google profile information
      await addUserAndDocsToDB({
        user,
        city: "", // Default empty, user can update later
        state: "", // Default empty, user can update later
      });
      console.log("User document created successfully for Google user");
    } else {
      console.log("User document already exists, sign-in complete");
    }

    return user;
  } catch (error) {
    console.error("Google sign-in error:", error);
    throw error;
  }
};

// Alternative email verification using Firebase Auth directly
export const sendEmailVerificationDirect = async (): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("No authenticated user found");
    }

    console.log("Sending email verification for user:", user.email);
    await firebaseSendEmailVerification(user);
    console.log("Email verification sent successfully");
  } catch (error) {
    console.error("Error sending email verification:", error);
    throw error;
  }
};

export default {
  createUser,
  sendEmailVerification,
  sendEmailVerificationDirect,
  signInWithGoogle,
};
